import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import SubscriptionOverview from './components/SubscriptionOverview';
import InvoiceTable from './components/InvoiceTable';
import PaymentMethodCard from './components/PaymentMethodCard';
import SubscriptionPlans from './components/SubscriptionPlans';
import NotificationSettings from './components/NotificationSettings';
import VehicleManagement from './components/VehicleManagement';
import SmsManagement from './components/SmsManagement';
import AuditLog from './components/AuditLog';
import ProfileSettings from './components/ProfileSettings';
import AccountSettings from './components/AccountSettings';
import ConnectedDevices from './components/ConnectedDevices';
import ConfirmationModal from '../../components/ConfirmationModal';
import { getSession } from '../../utils/auth';
import { trackSubscriptionCancellation } from '../../utils/notificationStore';
import { SubscriptionOverviewSkeleton, InvoiceTableSkeleton, PaymentMethodSkeleton, SubscriptionPlansSkeleton, VehicleManagementSkeleton, NotificationSettingsSkeleton } from '../../components/SkeletonLoader';

// Custom empty state for invoices
const InvoicesEmptyState = ({ onUpgrade }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '56px 24px',
      position: 'relative',
    }}
  >
    {/* Custom SVG illustration — invoice/document */}
    <svg width="120" height="110" viewBox="0 0 120 110" fill="none" style={{ marginBottom: '24px' }} aria-hidden="true">
      {/* Main document */}
      <rect x="30" y="10" width="60" height="80" rx="4" fill="rgba(0,79,152,0.08)" stroke="rgba(0,79,152,0.25)" strokeWidth="1.5"/>
      {/* Folded corner */}
      <path d="M75 10 L90 25 L75 25 Z" fill="rgba(0,79,152,0.15)" stroke="rgba(0,79,152,0.3)" strokeWidth="1"/>
      {/* Document lines */}
      <line x1="40" y1="38" x2="80" y2="38" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="40" y1="48" x2="72" y2="48" stroke="rgba(0,79,152,0.15)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="40" y1="58" x2="76" y2="58" stroke="rgba(0,79,152,0.15)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="40" y1="68" x2="65" y2="68" stroke="rgba(0,79,152,0.15)" strokeWidth="1.5" strokeLinecap="round"/>
      {/* NOK badge */}
      <circle cx="72" cy="78" r="14" fill="rgba(255,87,34,0.1)" stroke="rgba(255,87,34,0.35)" strokeWidth="1.5"/>
      <text x="72" y="83" textAnchor="middle" fill="rgba(255,87,34,0.7)" fontSize="10" fontWeight="700">kr</text>
      {/* Geometric accents */}
      <line x1="10" y1="15" x2="25" y2="15" stroke="rgba(255,87,34,0.3)" strokeWidth="1.5"/>
      <line x1="10" y1="15" x2="10" y2="30" stroke="rgba(255,87,34,0.3)" strokeWidth="1.5"/>
      <line x1="95" y1="80" x2="110" y2="80" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
      <line x1="110" y1="80" x2="110" y2="95" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
      {/* Stars/sparkles */}
      <circle cx="15" cy="70" r="2" fill="rgba(245,158,11,0.4)"/>
      <circle cx="22" cy="62" r="1.5" fill="rgba(245,158,11,0.3)"/>
      <circle cx="10" cy="58" r="1.5" fill="rgba(245,158,11,0.3)"/>
    </svg>
    <p style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '8px' }}>Ingen fakturaer ennå</p>
    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', maxWidth: '280px', lineHeight: 1.6, marginBottom: '24px' }}>Fakturaer vises her etter oppgradering til Premium</p>
    <button
      onClick={onUpgrade}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 22px',
        background: 'var(--color-accent)',
        border: '2px solid var(--color-accent)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-heading)',
        fontSize: '13px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#fff',
        cursor: 'pointer',
        transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,87,34,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
    >
      Oppgrader til Premium
    </button>
  </div>
);

const SubscriptionPortal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const session = getSession();
  const accountTier = session?.accountTier || 'free';
  const isPremium = accountTier === 'premium';
  const [isLoading, setIsLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState(false);

  const getInitialTab = () => {
    const params = new URLSearchParams(location?.search);
    return params?.get('tab') || 'overview';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const tab = params?.get('tab');
    if (tab) setActiveTab(tab);
  }, [location?.search]);

  const freeSubscription = {
    planName: 'Gratis Veiassistent',
    description: 'Grunnleggende tilgang til veiplanlegging',
    status: 'active',
    renewalDate: '—',
    monthlyPrice: '0 kr',
    activeServices: '2',
    benefits: [
      '10 bomberegninger per måned',
      'Grunnleggende trafikkinfo',
      'E-poststøtte',
    ]
  };

  const premiumSubscription = {
    planName: 'Premium Veiassistent',
    description: 'Komplett tilgang til alle tjenester og funksjoner',
    status: 'active',
    renewalDate: '15/03/2026',
    monthlyPrice: '499,00 kr',
    activeServices: '5',
    benefits: [
      'Ubegrenset bomkostnadsberegninger',
      'Sanntids trafikkoppdateringer',
      'EV/Hybrid rabattoptimalisering',
      'Prioritert kundeservice',
      'Månedlige kostnadsrapporter',
      'Automatisk ruteoptimalisering'
    ]
  };

  const subscription = isPremium ? premiumSubscription : freeSubscription;

  // Generate invoices dynamically based on subscription start date
  // For a new account, only 1 invoice is generated (current billing month)
  const generateInvoices = () => {
    if (!isPremium) return [];
    const subscriptionStartDate = session?.subscriptionStartDate
      ? new Date(session.subscriptionStartDate)
      : new Date(); // new accounts start today
    const today = new Date();
    const invoiceList = [];
    let current = new Date(today.getFullYear(), today.getMonth(), 1);
    let invoiceCount = 0;
    while (current >= new Date(subscriptionStartDate.getFullYear(), subscriptionStartDate.getMonth(), 1)) {
      const day = String(1)?.padStart(2, '0');
      const month = String(current?.getMonth() + 1)?.padStart(2, '0');
      const year = current?.getFullYear();
      invoiceCount++;
      invoiceList?.push({
        id: invoiceCount,
        invoiceNumber: `INV-${year}-${String(invoiceCount)?.padStart(3, '0')}`,
        date: `${day}/${month}/${year}`,
        amount: '499,00 kr',
        status: 'paid'
      });
      current?.setMonth(current?.getMonth() - 1);
      if (invoiceCount >= 24) break; // cap at 24 months
    }
    return invoiceList;
  };

  const invoices = generateInvoices();

  const paymentMethod = {
    type: 'visa',
    lastFour: '4242',
    expiryDate: '12/28',
    isDefault: true
  };

  const plans = [
    {
      id: 'free',
      name: 'Gratis',
      description: 'Grunnleggende funksjoner for sporadisk bruk',
      price: '0 kr',
      tier: 0,
      features: [
        '10 bomberegninger per måned',
        'Grunnleggende trafikkinfo',
        'E-poststøtte',
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Komplett tilgang til alle tjenester',
      price: '499,00 kr',
      annualPrice: '4 990,00 kr',
      savings: '998,00 kr',
      tier: 2,
      isPopular: true,
      features: [
        'Ubegrensede bomberegninger',
        'Sanntids trafikkoppdateringer',
        'EV/Hybrid rabattoptimalisering',
        'Prioritert kundeservice',
        'Månedlige kostnadsrapporter',
        'Automatisk ruteoptimalisering'
      ]
    },
    {
      id: 'enterprise',
      name: 'Bedrift',
      description: 'Skreddersydde løsninger for flåter',
      price: '1 499,00 kr',
      annualPrice: '14 990,00 kr',
      savings: '3 498,00 kr',
      tier: 3,
      features: [
        'Alt i Premium',
        'Flåtestyring for opptil 50 kjøretøy',
        'Dedikert kontoadministrator',
        'API-tilgang',
        'Tilpassede rapporter',
        '24/7 telefonsupport',
        'Fakturering med 30 dagers kreditt'
      ]
    }
  ];

  const notificationSettings = {
    emailInvoiceReminders: true,
    emailSubscriptionUpdates: true,
    emailMarketing: false,
    smsPaymentConfirmations: true,
    smsExpiryAlerts: true,
    pushTrafficAlerts: true,
    pushTollPassages: false
  };

  const vehicles = [
    { id: 1, registrationNumber: 'EL12345', nickname: 'Min Tesla', type: 'electric', discount: '100%', isDefault: true },
    { id: 2, registrationNumber: 'HY67890', nickname: 'Familiebilen', type: 'hybrid', discount: '50%', isDefault: false }
  ];

  const tabs = [
    { id: 'overview', label: 'Oversikt', icon: 'LayoutDashboard' },
    { id: 'profile', label: 'Profil', icon: 'User' },
    { id: 'invoices', label: 'Fakturaer', icon: 'FileText' },
    { id: 'payment', label: 'Betaling', icon: 'CreditCard' },
    { id: 'plans', label: 'Planer', icon: 'Package' },
    { id: 'vehicles', label: 'Kjøretøy', icon: 'Car' },
    { id: 'notifications', label: 'Varsler', icon: 'Bell' },
    { id: 'sms', label: 'SMS', icon: 'MessageSquare' },
    { id: 'devices', label: 'Enheter', icon: 'Monitor' },
    { id: 'audit', label: 'Revisjonslogg', icon: 'ClipboardList' },
    { id: 'settings', label: 'Innstillinger', icon: 'Settings' },
  ];

  const handleUpgrade = (planId) => {
    if (planId === 'premium' || planId === 'enterprise') {
      navigate('/payment-invoice-form?upgrade=' + planId);
    }
  };

  const handleCancelSubscription = () => {
    setCancelModal(true);
  };

  const handleCancelConfirm = () => {
    setCancelModal(false);
    // Subscription cancellation logic would go here
    console.log('Subscription cancelled');
    trackSubscriptionCancellation();
  };

  const handleCancelModalClose = () => {
    setCancelModal(false);
  };

  const currentPlanId = isPremium ? 'premium' : 'free';

  return (
    <>
      <Helmet>
        <title>Abonnementsportal - Veiassistent</title>
        <meta name="description" content="Administrer ditt Veiassistent-abonnement, fakturaer og betalingsmetoder" />
      </Helmet>
      <Header />
      <ConfirmationModal
        isOpen={cancelModal}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        title="Avslutt abonnement"
        message="Er du sikker på at du vil avslutte ditt Premium-abonnement? Du vil miste tilgang til alle Premium-funksjoner ved slutten av inneværende faktureringsperiode."
        consequence="Du mister tilgang til ubegrensede bomberegninger, sanntids trafikkoppdateringer, EV/Hybrid-rabatter og prioritert kundeservice."
        confirmLabel="Avslutt abonnement"
        cancelLabel="Avbryt"
        isDanger={true}
      />
      <main className="min-h-screen bg-background pt-[84px] pb-16 px-4 md:px-6 lg:px-8" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Architectural background accents */}
        <div style={{ position: 'absolute', top: '60px', right: '-100px', width: '400px', height: '400px', border: '2px solid rgba(0,79,152,0.06)', borderRadius: '2px', transform: 'rotate(12deg)', pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '200px', left: '-80px', width: '280px', height: '280px', border: '2px solid rgba(255,87,34,0.05)', borderRadius: '2px', transform: 'rotate(-8deg)', pointerEvents: 'none', zIndex: 0 }} />

        <div className="max-w-[1440px] mx-auto" style={{ position: 'relative', zIndex: 1 }}>
          {/* Asymmetrical header */}
          <div className="mb-8 lg:mb-12 subscription-header-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'flex-end', gap: '24px' }}>
            <div>
              <div style={{ display: 'inline-block', background: isPremium ? 'rgba(245,158,11,0.15)' : 'rgba(156,163,175,0.1)', border: `2px solid ${isPremium ? 'rgba(245,158,11,0.3)' : 'rgba(156,163,175,0.2)'}`, borderRadius: 'var(--radius-sm)', padding: '4px 14px', marginBottom: '16px' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: isPremium ? '#F59E0B' : 'var(--color-muted-foreground)' }}>
                  {isPremium ? '★ Premium-konto' : 'Gratis konto'}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-3 md:mb-4">
                Abonnementsportal
              </h1>
              <p className="text-base md:text-lg text-muted-foreground font-caption max-w-3xl">
                Administrer ditt abonnement, fakturaer og kontoinnstillinger
              </p>
            </div>
            {!isPremium && (
              <div style={{ flexShrink: 0 }}>
                <button
                  onClick={() => setActiveTab('plans')}
                  style={{
                    background: 'var(--color-accent)',
                    border: '2px solid var(--color-accent)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '12px 24px',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '13px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,87,34,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
                  onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                >
                  Oppgrader plan
                </button>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="mb-8 lg:mb-12 subscription-tab-nav overflow-x-auto">
            <div className="flex gap-2 min-w-max lg:min-w-0">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`nav-link flex-shrink-0 ${activeTab === tab?.id ? 'active' : ''}`}
                  aria-current={activeTab === tab?.id ? 'page' : undefined}
                  style={{ transition: 'transform 150ms cubic-bezier(0.22,1,0.36,1)' }}
                  onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
                  onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span className="flex items-center gap-2">
                    {tab?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6 lg:space-y-8">
            {activeTab === 'overview' && (
              isLoading ? <SubscriptionOverviewSkeleton /> : <SubscriptionOverview subscription={subscription} isPremium={isPremium} onUpgrade={() => setActiveTab('plans')} onCancelSubscription={isPremium ? handleCancelSubscription : undefined} />
            )}

            {activeTab === 'profile' && (
              <ProfileSettings />
            )}

            {activeTab === 'invoices' && (
              isLoading ? <InvoiceTableSkeleton /> : (
                <div className="card">
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-6 lg:mb-8">
                    Fakturahistorikk
                  </h2>
                  {invoices?.length > 0 ? (
                    <InvoiceTable invoices={invoices} />
                  ) : (
                    <InvoicesEmptyState onUpgrade={() => setActiveTab('plans')} />
                  )}
                </div>
              )
            )}

            {activeTab === 'payment' && (
              isLoading ? <PaymentMethodSkeleton /> : <PaymentMethodCard paymentMethod={paymentMethod} />
            )}

            {activeTab === 'plans' && (
              isLoading ? <SubscriptionPlansSkeleton /> : (
                <div>
                  <div className="mb-6 lg:mb-8">
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-3">
                      Velg din plan
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground font-caption">
                      Oppgrader eller endre ditt abonnement når som helst
                    </p>
                  </div>
                  <SubscriptionPlans
                    plans={plans}
                    currentPlanId={currentPlanId}
                    onUpgrade={handleUpgrade}
                  />
                </div>
              )
            )}

            {activeTab === 'vehicles' && (
              isLoading ? <VehicleManagementSkeleton /> : <VehicleManagement vehicles={vehicles} />
            )}

            {activeTab === 'notifications' && (
              isLoading ? <NotificationSettingsSkeleton /> : <NotificationSettings initialSettings={notificationSettings} />
            )}

            {activeTab === 'sms' && (
              <SmsManagement />
            )}

            {activeTab === 'audit' && (
              <AuditLog />
            )}

            {activeTab === 'devices' && (
              <ConnectedDevices />
            )}

            {activeTab === 'settings' && (
              <AccountSettings />
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SubscriptionPortal;